import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Alert,
  Snackbar,
  Avatar,
  CircularProgress,
  useMediaQuery,
  TextField,
  TableSortLabel,
  TablePagination,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Link,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { getProducts, getMedias, getCategoryTree, purgeAllLegacySKUs } from '../../../app/api';
import { useTranslation } from '../../../app/LanguageContext';
import ConfirmDialog from '../../core/public/ConfirmDialog';
import { ExtensionSlot } from '../../core/public/ExtensionSlot';
import { PRODUCT_DEFAULT_IMAGE } from '../../medias/public/MediaGallery';
import { isImageMimeOrExtension } from '../../medias/public/mediaMime';
import { formatListSkuMoneyRange } from '../private/productVariantUtils';
import { flattenCategoryTree, normalizeCategoryTreeNode } from '../../categories/public/categoryUtils';
import { subtleSurfaceBg } from '../../../theme/surfaceSx';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';
const SEARCH_DEBOUNCE_MS = 300;
const DEFAULT_ROWS_PER_PAGE = 25;

const COMPACT_HEAD_CELL_SX = {
  py: 0.75,
  px: 1,
  fontSize: '0.75rem',
  fontWeight: 600,
  whiteSpace: 'nowrap',
};

const COMPACT_BODY_CELL_SX = {
  py: 0.75,
  px: 1,
  fontSize: '0.8125rem',
};

const ROW_DATA_CELL_SX = {
  ...COMPACT_BODY_CELL_SX,
  pointerEvents: 'none',
};

const ROW_ACTIONS_CELL_SX = {
  ...COMPACT_BODY_CELL_SX,
  position: 'relative',
  zIndex: 2,
  pointerEvents: 'auto',
};

const ROW_LINK_OVERLAY_SX = {
  position: 'absolute',
  inset: 0,
  zIndex: 1,
  display: 'block',
  color: 'inherit',
  textDecoration: 'none',
  pointerEvents: 'auto',
};

const STATUS_FILTERS = [
  { value: 'ALL', labelKey: 'products.filter_all' },
  { value: 'ACTIVE', labelKey: 'products.active' },
  { value: 'INACTIVE', labelKey: 'products.inactive' },
];

const SORT_COLUMNS = [
  { id: 'name', labelKey: 'products.list_sort_name' },
  { id: 'code', labelKey: 'products.list_sort_code' },
  { id: 'status', labelKey: 'products.list_sort_status' },
  { id: 'sku_count', labelKey: 'products.list_sort_variations' },
  { id: 'updated_at', labelKey: 'products.list_sort_updated' },
];

const ProductsPage = () => {
  const { translate } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [productImages, setProductImages] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [categoryFilterId, setCategoryFilterId] = useState('');
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortField, setSortField] = useState('updated_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const searchDebounceRef = useRef(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [actionsMenuAnchor, setActionsMenuAnchor] = useState(null);
  const [confirmPurgeAllLegacy, setConfirmPurgeAllLegacy] = useState(false);
  const [purgingAllLegacy, setPurgingAllLegacy] = useState(false);

  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
      setPage(0);
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [searchInput]);

  useEffect(() => {
    (async () => {
      try {
        const res = await getCategoryTree();
        const tree = (res.data?.tree ?? res.data?.Tree ?? []).map(normalizeCategoryTreeNode).filter(Boolean);
        setCategoryOptions(flattenCategoryTree(tree));
      } catch {
        setCategoryOptions([]);
      }
    })();
  }, []);

  const loadProducts = useCallback(async () => {
    try {
      setLoading(true);
      const query = {
        sort: sortField,
        order: sortOrder,
        page: page + 1,
        limit: rowsPerPage,
      };
      if (debouncedSearch) query.q = debouncedSearch;
      if (statusFilter !== 'ALL') query.status = statusFilter;
      if (categoryFilterId) query.category_id = categoryFilterId;

      const [productsResponse, mediasResponse] = await Promise.all([
        getProducts(query),
        getMedias(),
      ]);
      const productsList = productsResponse.data.products || [];
      setTotal(productsResponse.data.total ?? productsList.length);
      const allMedias = mediasResponse.data.medias || [];
      const imagesMap = {};

      productsList.forEach((product) => {
        const productMedias = allMedias
          .map((media) => {
            if (!media.metadata?.length) return null;
            const matchingMeta = media.metadata.find((meta) => meta.key === 'product_image');
            if (!matchingMeta) return null;
            const metaValue = matchingMeta.value;
            const isObject = typeof metaValue === 'object' && metaValue !== null;
            if (!isObject) {
              if (metaValue !== product.id) return null;
              return { ...media, order: 0 };
            }
            if (metaValue.id !== product.id) return null;
            return { ...media, order: metaValue?.order || 0 };
          })
          .filter(Boolean)
          .sort((a, b) => (a.order || 0) - (b.order || 0));

        const thumb = productMedias.find((m) =>
          isImageMimeOrExtension(m.mime_type, m.filename),
        );
        if (thumb) {
          imagesMap[product.id] = `${API_URL}${thumb.file_path}`;
        }
      });

      setProductImages(imagesMap);
      setProducts(productsList);
    } catch (error) {
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, statusFilter, categoryFilterId, sortField, sortOrder, page, rowsPerPage]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const handleSort = (columnId) => {
    if (sortField === columnId) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(columnId);
      setSortOrder('asc');
    }
    setPage(0);
  };

  const getStatusColor = (status) => (status === 'ACTIVE' ? 'success' : 'default');
  const getStatusLabel = (status) =>
    status === 'ACTIVE' ? translate('products.active') : translate('products.inactive');

  const getSkuCount = (product) => product.sku_count ?? product.skuCount ?? 0;

  const getProductDisplayName = (product) => {
    const name = (product?.name ?? '').trim();
    return name || translate('products.unnamed_label');
  };

  const getMainCategoryName = (product) => {
    const main = product.main_category ?? product.mainCategory;
    if (main?.name) return main.name;
    return '—';
  };

  const formatListUpdatedAt = (value) => {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString(undefined, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const renderListStock = (product) => {
    const skuCount = getSkuCount(product);
    if (!skuCount) return '—';
    if (product.all_made_to_order ?? product.allMadeToOrder) {
      return translate('products.made_to_order_label');
    }
    const total = Number(product.stock_total ?? product.stockTotal ?? 0);
    if (!Number.isFinite(total)) return '—';
    return String(total % 1 === 0 ? total : Math.round(total * 100) / 100);
  };

  const buildMobileListMetaLine = (product) => {
    const segments = [
      `${getSkuCount(product)} ${translate('products.variants').toLowerCase()}`,
    ];
    const costText = formatListSkuMoneyRange(product, 'cost');
    if (costText) {
      segments.push(`${translate('products.list_cost')}: ${costText}`);
    }
    const priceText = formatListSkuMoneyRange(product, 'price');
    if (priceText) {
      segments.push(`${translate('products.price')}: ${priceText}`);
    }
    segments.push(formatListUpdatedAt(product.updated_at ?? product.updatedAt));
    segments.push(renderListStock(product));
    return segments.join(' · ');
  };

  const renderSortHeader = (column, sx = {}) => (
    <TableCell
      key={column.id}
      sortDirection={sortField === column.id ? sortOrder : false}
      sx={{ ...COMPACT_HEAD_CELL_SX, ...sx }}
    >
      <TableSortLabel
        active={sortField === column.id}
        direction={sortField === column.id ? sortOrder : 'asc'}
        onClick={() => handleSort(column.id)}
        sx={{ '& .MuiTableSortLabel-icon': { fontSize: '1rem' } }}
      >
        {translate(column.labelKey)}
      </TableSortLabel>
    </TableCell>
  );

  const productEditPath = (product) =>
    `/products/${encodeURIComponent(product.code || product.id)}/edit`;

  const productViewPath = (product) =>
    `/products/${encodeURIComponent(product.code || product.id)}/view`;

  const renderProductRowLink = (product, sx = ROW_LINK_OVERLAY_SX) => (
    <Link
      component={RouterLink}
      to={productViewPath(product)}
      aria-label={getProductDisplayName(product)}
      sx={sx}
    />
  );

  const renderStatusChip = (status, size = 'small') => (
    <Chip label={getStatusLabel(status)} size={size} color={getStatusColor(status)} sx={{ height: 22, fontSize: '0.7rem' }} />
  );

  const handlePurgeAllLegacy = async () => {
    setPurgingAllLegacy(true);
    try {
      const res = await purgeAllLegacySKUs();
      const data = res.data ?? {};
      setConfirmPurgeAllLegacy(false);
      await loadProducts();
      const msg = translate('products.purge_all_unused_success')
        .replace('{deleted}', String(data.deleted ?? 0))
        .replace('{products}', String(data.products_processed ?? 0))
        .replace('{retained}', String(data.retained ?? 0));
      setSnackbar({ open: true, message: msg, severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    } finally {
      setPurgingAllLegacy(false);
    }
  };

  const renderFilterChip = (label, selected, onClick) => (
    <Chip
      label={label}
      size="small"
      variant={selected ? 'filled' : 'outlined'}
      color={selected ? 'primary' : 'default'}
      onClick={onClick}
      sx={{ cursor: 'pointer', height: 24, fontSize: '0.75rem' }}
    />
  );

  return (
    <Box>
      <Box
        component="header"
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1,
          py: 1,
          px: 1.5,
          mb: 1.5,
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: subtleSurfaceBg,
        }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center', flex: 1, minWidth: 0 }}>
          {STATUS_FILTERS.map((f) =>
            renderFilterChip(translate(f.labelKey), statusFilter === f.value, () => {
              setStatusFilter(f.value);
              setPage(0);
            })
          )}
          <TextField
            size="small"
            placeholder={translate('products.list_search_hint')}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            sx={{
              flex: 1,
              minWidth: 160,
              maxWidth: { xs: '100%', md: 280 },
              '& .MuiInputBase-root': { height: 32, fontSize: '0.8125rem' },
            }}
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
          <ExtensionSlot slotId="products.list.actions" />
          <Button
            size="small"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/products/new')}
          >
            {translate('common.new')} {translate('products.title')}
          </Button>
          <IconButton
            size="small"
            aria-label={translate('common.actions')}
            onClick={(event) => setActionsMenuAnchor(event.currentTarget)}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
          <Menu
            anchorEl={actionsMenuAnchor}
            open={Boolean(actionsMenuAnchor)}
            onClose={() => setActionsMenuAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem
              onClick={() => {
                setActionsMenuAnchor(null);
                setConfirmPurgeAllLegacy(true);
              }}
            >
              {translate('products.purge_all_unused_variants')}
            </MenuItem>
          </Menu>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center', width: '100%' }}>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.25, flexShrink: 0 }}>
            {translate('products.category_filter_label')}
          </Typography>
          {renderFilterChip(translate('products.filter_all_categories'), !categoryFilterId, () => {
            setCategoryFilterId('');
            setPage(0);
          })}
          {categoryOptions.map((cat) =>
            renderFilterChip(cat.label || cat.name, categoryFilterId === cat.id, () => {
              setCategoryFilterId(cat.id);
              setPage(0);
            })
          )}
        </Box>
      </Box>

      {isMobile ? (
        loading ? (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <CircularProgress />
          </Paper>
        ) : products.length === 0 ? (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">{translate('products.no_products')}</Typography>
          </Paper>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {products.map((product) => (
              <Paper
                key={product.id}
                sx={{ position: 'relative', p: 1.5, cursor: 'pointer' }}
              >
                {renderProductRowLink(product, { ...ROW_LINK_OVERLAY_SX, borderRadius: 1 })}
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', pointerEvents: 'none', position: 'relative' }}>
                  <Avatar
                    src={productImages[product.id] || PRODUCT_DEFAULT_IMAGE}
                    variant="rounded"
                    sx={{ width: 72, height: 72 }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2" noWrap fontWeight={600}>
                      {getProductDisplayName(product)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {product.code}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                      {getMainCategoryName(product) !== '—' && (
                        <Chip label={getMainCategoryName(product)} size="small" variant="outlined" />
                      )}
                      <Chip label={getStatusLabel(product.status)} size="small" color={getStatusColor(product.status)} />
                      <Typography variant="caption" color="text.secondary">
                        {buildMobileListMetaLine(product)}
                      </Typography>
                    </Box>
                  </Box>
                  <Tooltip title={translate('common.edit')}>
                    <IconButton
                      size="small"
                      aria-label={translate('common.edit')}
                      sx={{ position: 'relative', zIndex: 2, pointerEvents: 'auto' }}
                      onClick={() => navigate(productEditPath(product))}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Paper>
            ))}
          </Box>
        )
      ) : (
        <TableContainer component={Paper} sx={{ width: '100%', overflowX: 'auto' }}>
          <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...COMPACT_HEAD_CELL_SX, width: 80, px: 0.75 }} />
                {renderSortHeader(SORT_COLUMNS[0], { width: '22%' })}
                <TableCell sx={{ ...COMPACT_HEAD_CELL_SX, width: '12%' }}>{translate('products.main_category')}</TableCell>
                {renderSortHeader(SORT_COLUMNS[2], { width: '10%' })}
                {renderSortHeader(SORT_COLUMNS[3], { width: '8%', textAlign: 'right' })}
                <TableCell sx={{ ...COMPACT_HEAD_CELL_SX, width: '10%', textAlign: 'right' }}>
                  {translate('products.list_cost')}
                </TableCell>
                <TableCell sx={{ ...COMPACT_HEAD_CELL_SX, width: '10%', textAlign: 'right' }}>
                  {translate('products.price')}
                </TableCell>
                {renderSortHeader(SORT_COLUMNS[4], { width: '12%' })}
                <TableCell sx={{ ...COMPACT_HEAD_CELL_SX, width: '8%', textAlign: 'right' }}>{translate('products.stock')}</TableCell>
                <TableCell sx={{ ...COMPACT_HEAD_CELL_SX, width: 48, px: 0.5 }} align="right">
                  {translate('common.actions')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    {translate('common.loading')}
                  </TableCell>
                </TableRow>
              ) : products.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    {translate('products.no_products')}
                  </TableCell>
                </TableRow>
              ) : (
                products.map((product) => (
                  <TableRow
                    key={product.id}
                    hover
                    sx={{ position: 'relative', cursor: 'pointer' }}
                  >
                    <TableCell sx={{ ...ROW_DATA_CELL_SX, px: 0.75 }}>
                      {renderProductRowLink(product)}
                      <Avatar
                        src={productImages[product.id] || PRODUCT_DEFAULT_IMAGE}
                        variant="rounded"
                        sx={{ width: 64, height: 64 }}
                      />
                    </TableCell>
                    <TableCell sx={ROW_DATA_CELL_SX}>
                      <Typography variant="body2" fontWeight={600} noWrap title={getProductDisplayName(product)}>
                        {getProductDisplayName(product)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap display="block">
                        {product.code}
                      </Typography>
                    </TableCell>
                    <TableCell sx={ROW_DATA_CELL_SX}>
                      <Typography variant="caption" color="text.secondary" noWrap title={getMainCategoryName(product)}>
                        {getMainCategoryName(product)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={ROW_DATA_CELL_SX}>{renderStatusChip(product.status)}</TableCell>
                    <TableCell sx={{ ...ROW_DATA_CELL_SX, textAlign: 'right' }}>{getSkuCount(product)}</TableCell>
                    <TableCell sx={{ ...ROW_DATA_CELL_SX, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {formatListSkuMoneyRange(product, 'cost')}
                    </TableCell>
                    <TableCell sx={{ ...ROW_DATA_CELL_SX, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {formatListSkuMoneyRange(product, 'price')}
                    </TableCell>
                    <TableCell sx={ROW_DATA_CELL_SX}>{formatListUpdatedAt(product.updated_at ?? product.updatedAt)}</TableCell>
                    <TableCell sx={{ ...ROW_DATA_CELL_SX, textAlign: 'right' }}>{renderListStock(product)}</TableCell>
                    <TableCell sx={{ ...ROW_ACTIONS_CELL_SX, px: 0.5 }} align="right">
                      <Tooltip title={translate('common.edit')}>
                        <IconButton
                          size="small"
                          sx={{ p: '6px' }}
                          aria-label={translate('common.edit')}
                          onClick={() => navigate(productEditPath(product))}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, nextPage) => setPage(nextPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage={translate('orders.pagination_rows_per_page')}
          />
        </TableContainer>
      )}

      <ConfirmDialog
        open={confirmPurgeAllLegacy}
        title={translate('products.purge_all_unused_confirm_title')}
        message={translate('products.purge_all_unused_confirm_message')}
        confirmText={purgingAllLegacy ? translate('common.saving') : translate('products.purge_all_unused_variants')}
        cancelText={translate('common.cancel')}
        confirmColor="warning"
        onConfirm={() => {
          if (!purgingAllLegacy) void handlePurgeAllLegacy();
        }}
        onCancel={() => setConfirmPurgeAllLegacy(false)}
      />

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default ProductsPage;
